import type { ContactInfo, NavCategory, NavIndustry, NavSubCategory, SocialLink } from './types'

/** Brand constants. Update `url` to the official domain before production launch. */
export const brand = {
  name: 'True Forge',
  legalName: 'True Forge',
  tagline: 'Forged for Safety',
  shortDescription:
    'Premium industrial safety equipment engineered for the toughest industries.',
  description:
    'True Forge manufactures premium industrial safety equipment — welding gloves, driver gloves, leather garments and accessories — engineered for welding, steel, oil & gas, construction and heavy industry.',
  logoWordmark: '/brand/logo-wordmark.png',
  /** Full emblem (PNG) — used for og:image / structured data. */
  logoBadge: '/brand/badge.png',
  /** Optimized emblem (18 KB webp) — used in the header/footer UI. */
  logoMark: '/brand/logo-mark.webp',
  url: 'https://www.true-forge.com',
} as const

/** Live contact info. Map is enabled and pinned to Houston, TX. */
export const contact: ContactInfo = {
  address: 'Houston, TX, USA',
  phone: '+1 (713) 543-1412',
  // phoneHref and whatsapp are COMPLETE hrefs — use them as-is, never re-prefix
  phoneHref: 'tel:+17135431412',
  email: 'Info@true-forge.com',
  whatsapp: 'https://wa.me/17135431412',
  hours: 'Mon–Fri: 9:00 AM – 5:00 PM',
  mapsEnabled: true,
  mapEmbedUrl: 'https://www.google.com/maps?q=Houston,+TX&output=embed',
}

/**
 * Primary product categories (SOW taxonomy). Welding Gloves is a parent category
 * with subcategories (see `subCategories`). `comingSoon` = no assets yet — the
 * card renders a premium placeholder. Slugs are fixed and future-proof.
 */
export const categories: NavCategory[] = [
  { slug: 'welding-gloves', name: 'Welding Gloves' },
  { slug: 'driver-gloves', name: 'Driver Gloves' },
  { slug: 'mechanic-gloves', name: 'Mechanic Gloves', comingSoon: true },
  { slug: 'general-purpose-gloves', name: 'General Purpose Gloves' },
  { slug: 'welding-caps', name: 'Welding Caps' },
  { slug: 'leather-garments', name: 'Leather Garments & Accessories' },
]

/** Subcategories nested under a primary category (Welding Gloves → TIG/MIG, Stick). */
export const subCategories: NavSubCategory[] = [
  { slug: 'tig-mig', name: 'TIG / MIG Welding Gloves', category: 'welding-gloves' },
  { slug: 'stick', name: 'Stick Welding Gloves', category: 'welding-gloves' },
]

/** Industries We Serve (Scope of Work). */
export const industries: NavIndustry[] = [
  { slug: 'welding-fabrication', name: 'Welding & Fabrication' },
  { slug: 'construction', name: 'Construction' },
  { slug: 'steel-industry', name: 'Steel Industry' },
  { slug: 'manufacturing', name: 'Manufacturing' },
  { slug: 'oil-gas', name: 'Oil & Gas' },
  { slug: 'automotive', name: 'Automotive' },
  { slug: 'shipbuilding', name: 'Shipbuilding' },
  { slug: 'mining', name: 'Mining' },
]

export const social: SocialLink[] = [
  { label: 'LinkedIn', href: '#' },
  { label: 'Instagram', href: '#' },
  { label: 'Facebook', href: '#' },
]

export const resourceLinks = [
  { label: 'Articles', to: '/articles' },
  { label: 'FAQ', to: '/faq' },
  // Hidden until catalogs are ready — restore when client provides spec sheets.
  // { label: 'Downloads', to: '/downloads' },
]

export const legalLinks = [
  { label: 'Privacy Policy', to: '/privacy' },
  { label: 'Terms & Conditions', to: '/terms' },
]

/** Helpers */
export const getCategory = (slug?: string): NavCategory | undefined =>
  categories.find((c) => c.slug === slug)

export const subCategoriesOf = (categorySlug: string): NavSubCategory[] =>
  subCategories.filter((s) => s.category === categorySlug)

export const getSubCategory = (categorySlug?: string, subSlug?: string): NavSubCategory | undefined =>
  subCategories.find((s) => s.category === categorySlug && s.slug === subSlug)

/** True if a category has any subcategories (e.g. Welding Gloves). */
export const hasSubCategories = (categorySlug: string): boolean =>
  subCategories.some((s) => s.category === categorySlug)

export const getIndustry = (slug?: string): NavIndustry | undefined =>
  industries.find((i) => i.slug === slug)
